import { useTranslation } from 'react-i18next';

import { VIBRATO_DEPTH_REFERENCE_CENTS } from '@muzly/voice-engine';
import type { PitchSeries } from '@muzly/voice-engine';

import {
  linePath,
  smoothPath,
  vibratoTrace,
  vibratoWave,
} from './chartGeometry';

/**
 * 떨림 파형 (시안 §8.6 `994:7699`) — 3초 창, 세로 ±100 cents, 참고 구간(±50~120)은
 * 연분홍 띠, 위 오른쪽에 초록 주석.
 *
 * 그 회차의 음정 곡선(`series`)이 있으면 **그 테이크의 떨림 3초**를 그린다(`vibratoTrace`,
 * TestFlight #57). 없으면(서버 복원·이 필드 이전 회차) 속도·폭 두 숫자로 그린 모식도이고,
 * 아래 한 줄이 어느 쪽인지 말한다 — 모식도를 녹음의 그림으로 읽히게 두면 안 된다.
 * 시안의 `비브라토 시작 0.9s` 주석은 시작 시각을 저장하지 않아 그리지 않는다.
 */

const SPAN_SEC = 3;
const SPAN_CENTS = 100;

const W = 339;
const H = 180;
const PLOT = { l: 42, r: 16, t: 34, b: 30 };
const MID = PLOT.t + (H - PLOT.t - PLOT.b) / 2;
const HALF = (H - PLOT.t - PLOT.b) / 2;

const yOfCents = (cents: number): number => MID - (cents / SPAN_CENTS) * HALF;
const xOfSec = (sec: number): number =>
  PLOT.l + (sec / SPAN_SEC) * (W - PLOT.l - PLOT.r);

export const VibratoWaveChart = ({
  rateHz,
  depthCents,
  series,
}: {
  rateHz: number;
  depthCents: number;
  /** 그 회차의 음정 곡선. 있으면 모식도 대신 실측 떨림을 그린다. */
  series?: PitchSeries;
}) => {
  const { t } = useTranslation();

  // 숫자가 아니면 아예 안 그린다. 옛 스키마나 손상된 회차가 필드 하나를 빠뜨리면
  // 좌표가 `NaN`이 되고, SVG는 그 path를 통째로 버려 **빈 카드**만 남는다.
  if (!Number.isFinite(rateHz) || !Number.isFinite(depthCents)) {
    return null;
  }

  const trace = vibratoTrace(series, depthCents, SPAN_SEC, SPAN_CENTS);
  const wave = (
    trace ?? vibratoWave(rateHz, depthCents, SPAN_SEC, SPAN_CENTS)
  ).map((p) => ({
    x: xOfSec((p.x / 100) * SPAN_SEC),
    y: p.y === null ? null : yOfCents(((50 - p.y) / 50) * SPAN_CENTS),
  }));
  const { min, max } = VIBRATO_DEPTH_REFERENCE_CENTS;
  const bandTop = { y: yOfCents(Math.min(max, SPAN_CENTS)), h: 0 };
  bandTop.h = yOfCents(min) - bandTop.y;
  const bandBottom = { y: yOfCents(-min), h: 0 };
  bandBottom.h = yOfCents(-Math.min(max, SPAN_CENTS)) - bandBottom.y;

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={t(
          trace
            ? 'web.result.vibrato.chartLabelMeasured'
            : 'web.result.vibrato.chartLabel',
          {
            rate: rateHz.toFixed(1),
            depth: Math.round(depthCents),
          },
        )}
      >
        {/* 참고 구간 띠 — 위아래 대칭. 창(±100)을 넘는 부분은 잘린다. */}
        <rect
          x={PLOT.l}
          y={bandTop.y}
          width={W - PLOT.l - PLOT.r}
          height={bandTop.h}
          fill="hsl(var(--primary) / 0.08)"
        />
        <rect
          x={PLOT.l}
          y={bandBottom.y}
          width={W - PLOT.l - PLOT.r}
          height={bandBottom.h}
          fill="hsl(var(--primary) / 0.08)"
        />
        <text
          x={W - PLOT.r}
          y={PLOT.t - 12}
          textAnchor="end"
          fontSize={12}
          fontWeight={600}
          fill="var(--green)"
        >
          {t('web.result.vibrato.depthRef', { min, max })}
        </text>

        {/* 기준선 — 떨림이 없으면 곡선이 이 선과 겹친다. */}
        <line
          x1={PLOT.l}
          x2={W - PLOT.r}
          y1={MID}
          y2={MID}
          stroke="var(--bk-300)"
          strokeWidth={1}
        />
        {[SPAN_CENTS, 0, -SPAN_CENTS].map((cents) => (
          <text
            key={cents}
            x={PLOT.l - 12}
            y={yOfCents(cents) + 4}
            textAnchor="end"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {cents}
          </text>
        ))}
        <text
          x={PLOT.l - 12}
          y={yOfCents(SPAN_CENTS) + 18}
          textAnchor="end"
          fontSize={11}
          fill="var(--bk-500)"
        >
          {t('web.result.vibrato.centsUnit')}
        </text>

        <path
          data-testid="vibrato-wave"
          d={trace ? smoothPath(wave) : linePath(wave)}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {[0, 1, 2, 3].map((sec) => (
          <text
            key={sec}
            x={xOfSec(sec)}
            y={H - 8}
            textAnchor="middle"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {t('web.result.vibrato.axisSecShort', { value: sec })}
          </text>
        ))}
      </svg>
      {/* 이 한 줄이 없으면 모식도가 녹음의 그림으로 읽힌다(반대로 실측을 모식도라 부르면 거짓이다). */}
      <p className="mt-2 px-[18px] text-[11px] leading-relaxed text-bk-500">
        {t(
          trace
            ? 'web.result.vibrato.waveNoteMeasured'
            : 'web.result.vibrato.waveNote',
        )}
      </p>
    </div>
  );
};

/**
 * 폭 설명 행의 작은 파형(시안 `진동 폭 이해하기`) — 90×40, 진폭만 다르다.
 * 값이 아니라 **그림 설명**이라 회차와 무관하다.
 */
export const MiniWave = ({ amplitude }: { amplitude: number }) => {
  const points = Array.from({ length: 61 }, (_, i) => ({
    x: (i / 60) * 90,
    y: 20 - Math.sin((i / 60) * Math.PI * 6) * amplitude,
  }));
  return (
    <svg
      viewBox="0 0 90 40"
      className="h-10 w-[90px] shrink-0"
      aria-hidden="true"
    >
      <path
        d={linePath(points)}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
  );
};
